import { getFormulaTokens } from "@features/dynamic-variables/calc/evaluate-formula";
import {
  buildDateToken,
  parseDateToken,
} from "@features/dynamic-variables/date-variable/date-token";
import { omitHiddenSenderVariables } from "@/shared/data/document-info-element";
import { IFsVariableTypeEnum } from "@/shared/types/fs-variables.types";

export type VariableData = {
  name: string;
  type: string;
  [key: string]: unknown;
};
export type KnownVariable = { variable: VariableData };

type StoryLike = {
  slides?: unknown;
  settings?: { og?: { siteName?: string | null; description?: string | null } };
};

export const collectStoryVariables = ({
  story,
  knownVariables,
  repeatableKeys = [],
}: {
  story: StoryLike;
  knownVariables: KnownVariable[];
  repeatableKeys?: string[];
}): VariableData[] => {
  const wholeText = buildWholeText(story);
  const knownByName = new Map<string, VariableData>();
  for (const entry of knownVariables) {
    const name = entry?.variable?.name;
    if (name && !knownByName.has(name)) {
      knownByName.set(name, entry.variable);
    }
  }

  const isRepeatableToken = (name: string): boolean =>
    repeatableKeys.some((key) => name === key || name.startsWith(`${key}.`));

  const result: VariableData[] = [];

  // A LIST variable is bound to a container through `settings.listDynamicVariable`
  // (a bare name, no braces), so the `{{token}}` scan below never sees it and the
  // variable would be missing from the manifest entirely — which is why the share
  // dialog had no row to edit it. Collect those bindings explicitly.
  const listNames = new Set<string>();
  for (const name of extractListBindings(wholeText)) {
    if (isRepeatableToken(name)) continue;
    listNames.add(name);
    const known = knownByName.get(name);
    result.push(
      known ?? {
        name,
        type: "list",
        title: name,
        default: "",
        tip: "",
        validation: { required: false },
      },
    );
  }

  // A `data` variable bound to a table / chart through
  // `settings.dataDynamicVariable` keeps its `{{token}}` form, so the scan below
  // does see it — but knows nothing of its type and would synthesize a text
  // input. Collect the bindings first with the `data` type, so the share dialog
  // renders the data sheet for a variable a version created on the fly.
  for (const name of extractDataBindings(wholeText)) {
    if (isRepeatableToken(name)) continue;
    const known = knownByName.get(name);
    result.push(
      known ?? {
        name,
        type: "data",
        title: name,
        default: "",
        tip: "",
        validation: { required: false },
      },
    );
  }

  // Tokens namespaced to a list (`{{array.first_name}}`, inserted when a variable
  // is dropped inside a list-bound container — see insert/list-namespace.ts) are
  // ROW fields, not standalone variables: the list's own row-fill editor covers
  // them. Emitting them too would add junk rows to the share dialog. Same
  // treatment repeatable-namespaced tokens already get.
  const listPrefixes = Array.from(listNames).map((listName) => `${listName}.`);
  const isListScopedToken = (name: string): boolean =>
    listPrefixes.some((prefix) => name.startsWith(prefix));

  for (const { name, fallback, dateOffsets } of extractTokens(wholeText)) {
    if (isRepeatableToken(name)) continue;
    if (isListScopedToken(name)) continue;
    const known = knownByName.get(name);
    // The offsets the story actually uses on this date variable. The manifest
    // is keyed by the BARE name (the sender fills the base date once), so the
    // offsets would otherwise be lost — and the share dialog would label the
    // row with the catalogue's placeholder title "Date +Xd". Only attached
    // when there is something to attach, so an unrelated variable's entry
    // stays byte-identical (and `known` keeps its registry identity).
    if (known) {
      result.push(dateOffsets ? { ...known, dateOffsets } : known);
    } else {
      // Every token in the story is a variable — no namespace allowlist, so
      // new variable sources work without code changes. Tokens with no registry
      // record get a synthesized entry whose shape mirrors IFsVariableData, so
      // fs-vars consumers (share form, default values) read the same fields.
      result.push({
        name,
        // A token in an image / logo / video element's `dynamicVariableSrc`
        // is a media slot; typing it `input` would give the sender a text box
        // for an image.
        type: isMediaBound(wholeText, name)
          ? IFsVariableTypeEnum.ImageVideo
          : "input",
        title: name,
        default: fallback ?? "",
        tip: "",
        validation: { required: false },
        ...(dateOffsets ? { dateOffsets } : {}),
      });
    }
  }

  // A DERIVED variable references other variables that the deck itself may
  // never render, and it cannot produce a value until each of those has one —
  // so every referenced variable needs its own row in the share dialog:
  //  - an AI variable's `prompt` (`Write about {{industry}}`);
  //  - a CALCULATION variable's `formula` (`{{price}} * {{quantity}}`) — the
  //    operands. Without this the sender saw the calc pill on the deck but no
  //    inputs to drive it (the calc variable itself is never an editable row;
  //    version-vars.tsx filters `type: "calculation"` out).
  // Dependencies are inserted right BEFORE the variable that references them,
  // resolved through the registry or synthesized as an `input` like any other
  // token, with the same "already present wins / first name wins" rules.
  const withDependencies: VariableData[] = [];
  const present = new Set(result.map((variable) => variable.name));
  const pushDependency = (name: string) => {
    // A row field of a repeatable group or of a legacy list binding is filled
    // per row, never as a standalone top-level input.
    if (present.has(name) || isRepeatableToken(name) || isListScopedToken(name))
      return;
    present.add(name);
    withDependencies.push(
      knownByName.get(name) ?? {
        name,
        type: "input",
        title: name,
        default: "",
        tip: "",
        validation: { required: false },
      },
    );
  };
  for (const variable of result) {
    if (variable.type === "ai" && typeof variable.prompt === "string") {
      for (const { name } of extractTokens(variable.prompt)) {
        pushDependency(name);
      }
    }
    if (
      variable.type === "calculation" &&
      typeof variable.formula === "string"
    ) {
      // The calc grammar's own extractor, so only real `{{name}}` operands
      // count (numbers and operators are not variables).
      for (const name of getFormulaTokens(variable.formula)) {
        pushDependency(name);
      }
    }
    withDependencies.push(variable);
  }

  // One entry per variable: a list variable can be both bound to a container and
  // referenced by a token elsewhere in the deck.
  const byName = new Map<string, VariableData>();
  for (const variable of withDependencies) {
    if (!byName.has(variable.name)) byName.set(variable.name, variable);
  }
  return Array.from(byName.values());
};

const buildWholeText = (story: StoryLike): string => {
  // Document info keeps `{{sender_*}}` even with its "Sent by" pill off;
  // only tokens the story actually renders count as variables.
  const slides = Array.isArray(story.slides) ? story.slides : [];
  let wholeText = JSON.stringify(
    omitHiddenFullScreenMedia(omitHiddenSenderVariables(slides)),
  );
  const og = story.settings?.og;
  if (og) {
    wholeText += JSON.stringify({
      siteName: og.siteName || "",
      description: og.description || "",
    });
  }
  return wholeText;
};

type ScanNode = {
  type?: string;
  settings?: { name?: unknown };
  children?: ScanNode[];
};

// A FullScreen media column (the set's second group, `.group.pos-1`) renders
// only its first element: `[data-layer-type="element"]:not(:first-child)` is
// display:none there, with no breakpoint or print override. Anything stacked
// behind it can never show, so its tokens must not reach the share flow
// (Monday #13217975419). Copy-on-write: untouched subtrees keep identity.
const omitHiddenFullScreenMedia = (nodes: unknown[]): unknown[] => {
  let changed = false;
  const next = nodes.map((node) => {
    const out = trimNode(node as ScanNode);
    if (out !== node) changed = true;
    return out;
  });
  return changed ? next : nodes;
};

const trimNode = (node: ScanNode): ScanNode => {
  if (!node?.children?.length) return node;
  const isFullScreen =
    node.type === "combo" && node.settings?.name === "FullScreen";
  const children = isFullScreen
    ? node.children.map(trimMediaColumn)
    : (omitHiddenFullScreenMedia(node.children) as ScanNode[]);
  const same = children.every((child, i) => child === node.children?.[i]);
  return same ? node : { ...node, children };
};

const trimMediaColumn = (set: ScanNode): ScanNode => {
  const media = set.children?.[1];
  if (!media?.children || media.children.length < 2) return set;
  const children = [...(set.children ?? [])];
  children[1] = { ...media, children: media.children.slice(0, 1) };
  return { ...set, children };
};

/**
 * Names of list variables bound to a container via `settings.listDynamicVariable`.
 * Read off the stringified deck (same scan strategy as the token extractor), so
 * it needs no knowledge of the entity tree's shape.
 */
const extractListBindings = (content: string): string[] => {
  const regex = /"listDynamicVariable"\s*:\s*"([^"]+)"/g;
  const names = new Set<string>();
  let match: RegExpExecArray | null = regex.exec(content);
  while (match !== null) {
    // Tolerate a binding stored with braces.
    const name = match[1]
      .replace(/^\{\{+/, "")
      .replace(/\}\}+$/, "")
      .trim();
    if (name) names.add(name);
    match = regex.exec(content);
  }
  return Array.from(names);
};

/**
 * Names of `data` variables bound to a table / chart via
 * `settings.dataDynamicVariable` (stored as `{{token}}`, braces tolerated).
 */
const extractDataBindings = (content: string): string[] => {
  const regex = /"dataDynamicVariable"\s*:\s*"([^"]+)"/g;
  const names = new Set<string>();
  let match: RegExpExecArray | null = regex.exec(content);
  while (match !== null) {
    const name = match[1]
      .replace(/^\{\{+/, "")
      .replace(/\}\}+$/, "")
      .trim();
    if (name) names.add(name);
    match = regex.exec(content);
  }
  return Array.from(names);
};

/** Whether `{{name}}` is bound to a media element (`dynamicVariableSrc`, the
 * property the image, logo and video elements declare). */
const isMediaBound = (content: string, name: string): boolean =>
  content.includes(`"dynamicVariableSrc":"{{${name}}}"`);

/** A variable name: identifier characters, dots for namespaces
 * (`hubspot.deal.amount`, `system.current_date`). */
const VALID_TOKEN_NAME = /^[\w.-]+$/;

type ParsedToken = {
  name: string;
  fallback?: string;
  /**
   * Every distinct date offset the story applies to this variable, in
   * appearance order (`["+7d", "+1m"]`). Absent when the variable is only
   * ever used bare. The offset is stripped from `name` (one row per base
   * variable), so this is the only place that information survives — the
   * share dialog uses it to label the row `Date +7d` instead of the
   * catalogue's placeholder title `Date +Xd`.
   */
  dateOffsets?: string[];
};

// Every {{token}} / {{{token}}} in appearance order, unique by name. A token may
// carry a helper arg (`fallback="x"`); the name is the first word, and the inline
// fallback becomes a synthesized variable's default. A date token's offset and
// format (`{{kickoff+5d|D MMM YYYY}}`) are presentation of the SAME variable, so
// they are stripped from the name: the manifest (and the share dialog built from
// it) lists the bare variable once, never `kickoff+5d|D` as a second input. The
// offsets themselves are collected onto the entry (`dateOffsets`) rather than
// discarded, so a later occurrence of the same variable with a different offset
// still contributes its label.
const extractTokens = (content: string): ParsedToken[] => {
  const regex = /\{\{\{([^{}]+)\}\}\}|\{\{([^{}]+)\}\}/g;
  const byName = new Map<string, ParsedToken>();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    const raw = (match[1] || match[2]).trim();
    const { name, offset } = parseDateToken(raw.split("|")[0].split(/\s+/)[0]);
    // The scan runs over the JSON-stringified deck, so an unfinished `{{` left
    // in a text element pairs with the JSON's own closing `}}` and "matches"
    // `{{</p>","name":"RichText"}}` — which then surfaced as a variable in the
    // share dialog and the plan gate. Only identifier-shaped names count.
    if (!VALID_TOKEN_NAME.test(name)) continue;
    let token = byName.get(name);
    if (!token) {
      // Quotes may be JSON-escaped (\") because content is stringified before scan.
      const fallbackMatch = raw.match(/fallback=\\?["']([^"'\\]*)/);
      token = {
        name,
        fallback: fallbackMatch ? fallbackMatch[1] : undefined,
      };
      byName.set(name, token);
    }
    if (offset && offset.amount !== 0) {
      // `buildDateToken` with an empty name yields exactly the token suffix
      // ("+7d"), so the label can never drift from the token grammar.
      const suffix = buildDateToken({ name: "", offset, format: null });
      const offsets = (token.dateOffsets ??= []);
      if (!offsets.includes(suffix)) offsets.push(suffix);
    }
  }
  return Array.from(byName.values());
};
